import { ReactNode, useState, useEffect, useRef, useId } from 'react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { SelectField } from '@/components/SelectField'
import { PRICE_STEP } from '@/data/config'
import {
	MultiSelectContent,
	SingleSelectContent,
	RangeContent,
	DateContent
} from '@/components/FilterContent'
import type { FilterOption } from '@/components/FilterContent'
import type { CustomFilterConfig } from '@/types/api'

/* ---------- FilterCategory accordion ---------- */

interface FilterCategoryProps {
	id: string
	label: string
	valueSummary: string
	isExpanded: boolean
	onToggle: () => void
	children: ReactNode
}

function FilterCategory ({
	label,
	valueSummary,
	isExpanded,
	onToggle,
	children
}: FilterCategoryProps) {
	const panelId = useId()

	return (
		<div className='spaces:border-b spaces:border-canvas-3'>
			<button
				type='button'
				onClick={onToggle}
				aria-expanded={isExpanded}
				aria-controls={isExpanded ? panelId : undefined}
				className='spaces:flex spaces:items-center spaces:gap-8 spaces:w-full spaces:px-16 spaces:py-24 spaces:text-left spaces:cursor-pointer'
			>
				<span className='spaces:text-16 spaces:font-default spaces:text-content-1 spaces:whitespace-nowrap spaces:shrink-0'>
					{label}
				</span>
				<span className='spaces:flex-1 spaces:text-16 spaces:font-default spaces:text-content-3 spaces:text-right spaces:truncate spaces:min-w-0'>
					{valueSummary}
				</span>
				<Icon
					name={isExpanded ? 'CaretDown' : 'CaretRight'}
					size={20}
					className='spaces:text-content-3 spaces:shrink-0'
				/>
			</button>
			{isExpanded && <div id={panelId} className='spaces:px-16 spaces:pb-24'>{children}</div>}
		</div>
	)
}

/* ---------- AllFiltersModal ---------- */

interface SortOption {
	value: string
	label: string
}

export interface AllFiltersModalProps {
	open: boolean
	onClose: () => void
	/* Sort */
	sort: string
	onSortChange: (value: string) => void
	sortOptions: SortOption[]
	/* Beds */
	beds: string[]
	onBedsChange: (beds: string[]) => void
	bedOptions: FilterOption[]
	showPriceFilter?: boolean
	showDateFilter?: boolean
	/* Price */
	priceMin: number | undefined
	priceMax: number | undefined
	onPriceMinChange: (value: number | undefined) => void
	onPriceMaxChange: (value: number | undefined) => void
	priceMinBound: number
	priceMaxBound: number
	/* Move-In Date */
	moveIn: string
	onMoveInChange: (value: string) => void
	calendarMonth: number
	calendarYear: number
	onCalendarMonthChange: (month: number, year: number) => void
	/* Custom filters */
	customFilterConfigs: CustomFilterConfig[]
	customFilterSelections: Record<string, string[]>
	onCustomFilterSelectionsChange: (selections: Record<string, string[]>) => void
	/* Actions */
	onApply: () => void
	/** Called after Apply commits, with the new filter values and sort. */
	onFiltersApply?: (beds: string[], priceMin: number | undefined, priceMax: number | undefined, moveIn: string, sort: string, customSelections?: Record<string, string[]>) => void
}

export function AllFiltersModal ({
	open,
	onClose,
	sort,
	onSortChange,
	sortOptions,
	beds,
	onBedsChange,
	bedOptions,
	showPriceFilter = true,
	showDateFilter = true,
	priceMin,
	priceMax,
	onPriceMinChange,
	onPriceMaxChange,
	priceMinBound,
	priceMaxBound,
	moveIn,
	onMoveInChange,
	calendarMonth,
	calendarYear,
	onCalendarMonthChange,
	customFilterConfigs,
	customFilterSelections,
	onCustomFilterSelectionsChange,
	onApply,
	onFiltersApply
}: AllFiltersModalProps) {
	const [expanded, setExpanded] = useState<Set<string>>(new Set())

	/* --- Draft state (committed only on Apply) --- */
	const [draftSort, setDraftSort] = useState(sort)
	const [draftBeds, setDraftBeds] = useState(beds)
	const [draftPriceMin, setDraftPriceMin] = useState(priceMin)
	const [draftPriceMax, setDraftPriceMax] = useState(priceMax)
	const [draftMoveIn, setDraftMoveIn] = useState(moveIn)
	const [draftCalendarMonth, setDraftCalendarMonth] = useState(calendarMonth)
	const [draftCalendarYear, setDraftCalendarYear] = useState(calendarYear)
	const [draftCustomSelections, setDraftCustomSelections] = useState<Record<string, string[]>>(() => ({ ...customFilterSelections }))

	/* Snapshot committed values into drafts when the modal opens */
	const prevOpen = useRef(open)
	useEffect(() => {
		if (open && !prevOpen.current) {
			setDraftSort(sort)
			setDraftBeds(beds)
			setDraftPriceMin(priceMin)
			setDraftPriceMax(priceMax)
			setDraftMoveIn(moveIn)
			setDraftCalendarMonth(calendarMonth)
			setDraftCalendarYear(calendarYear)
			setDraftCustomSelections({ ...customFilterSelections })
		}
		prevOpen.current = open
	}, [
		open,
		sort,
		beds,
		priceMin,
		priceMax,
		moveIn,
		calendarMonth,
		calendarYear,
		customFilterSelections
	])

	/* Apply: commit all drafts to parent state, then close */
	const handleApply = () => {
		onBedsChange(draftBeds)
		onPriceMinChange(draftPriceMin)
		onPriceMaxChange(draftPriceMax)
		onMoveInChange(draftMoveIn)
		onCalendarMonthChange(draftCalendarMonth, draftCalendarYear)
		onCustomFilterSelectionsChange(draftCustomSelections)
		onFiltersApply?.(draftBeds, draftPriceMin, draftPriceMax, draftMoveIn, draftSort, draftCustomSelections)
		onApply()
	}

	/* Clear All: reset all drafts without committing */
	const handleClearAll = () => {
		setDraftBeds([])
		setDraftPriceMin(undefined)
		setDraftPriceMax(undefined)
		setDraftMoveIn('')
		const cleared: Record<string, string[]> = {}
		for (const cf of customFilterConfigs) cleared[cf.slug] = []
		setDraftCustomSelections(cleared)
	}

	const toggleCategory = (id: string) => {
		setExpanded(prev => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	/* Summary helpers — use draft values so the modal reflects pending changes */
	const bedsLabel = formatMultiLabel(draftBeds, bedOptions) ?? 'Any'
	const priceLabel =
		draftPriceMin != null && draftPriceMax != null
			? `$${draftPriceMin.toLocaleString()}–$${draftPriceMax.toLocaleString()}`
			: draftPriceMin != null
			? `$${draftPriceMin.toLocaleString()}+`
			: draftPriceMax != null
			? `Up to $${draftPriceMax.toLocaleString()}`
			: 'Any'
	const dateLabel = draftMoveIn ? formatShortDate(draftMoveIn) ?? 'Any' : 'Any'

	const footer = (
		<>
			<Button
				label='Clear All'
				appearance='ghost'
				color='content2'
				size='md'
				spacesClass='spaces-clear'
				onClick={handleClearAll}
			/>
			<Button
				label='Apply'
				appearance='solid'
				color='brand'
				size='md'
				spacesClass='spaces-apply'
				onClick={handleApply}
			/>
		</>
	)

	return (
		<Modal
			open={open}
			onClose={onClose}
			title='Filter & Sort'
			footer={footer}
			spacesClass='spaces-filters-modal'
		>
			<div className='spaces:-m-24 spaces:flex spaces:flex-col'>
				{/* Sort */}
				<div className='spaces:border-b spaces:border-canvas-3 spaces:px-16 spaces:py-24'>
					<SelectField
						options={sortOptions}
						value={draftSort}
						onValueChange={setDraftSort}
						label='Sort:'
						className='spaces:w-full spaces:border spaces:border-canvas-3'
						containerClassName='spaces:w-full'
						matchTriggerWidth
					/>
				</div>

				{/* Bedrooms */}
				<FilterCategory
					id='beds'
					label='Bedrooms'
					valueSummary={bedsLabel}
					isExpanded={expanded.has('beds')}
					onToggle={() => toggleCategory('beds')}
				>
					<MultiSelectContent
						options={bedOptions}
						selected={draftBeds}
						onChange={setDraftBeds}
						maxHeight={null}
						ariaLabel='Bedrooms'
					/>
				</FilterCategory>

				{/* Price */}
				{showPriceFilter && (
					<FilterCategory
						id='price'
						label='Price'
						valueSummary={priceLabel}
						isExpanded={expanded.has('price')}
						onToggle={() => toggleCategory('price')}
					>
						<RangeContent
							min={priceMinBound}
							max={priceMaxBound}
							valueMin={draftPriceMin}
							valueMax={draftPriceMax}
							onMinChange={setDraftPriceMin}
							onMaxChange={setDraftPriceMax}
							step={PRICE_STEP}
							prefix='$'
						/>
					</FilterCategory>
				)}

				{/* Move-In Date */}
				{showDateFilter && (
					<FilterCategory
						id='date'
						label='Move-In Date'
						valueSummary={dateLabel}
						isExpanded={expanded.has('date')}
						onToggle={() => toggleCategory('date')}
					>
						<DateContent
							value={draftMoveIn}
							onValueChange={setDraftMoveIn}
							calendarMonth={draftCalendarMonth}
							calendarYear={draftCalendarYear}
							onCalendarMonthChange={(month, year) => {
								setDraftCalendarMonth(month)
								setDraftCalendarYear(year)
							}}
						/>
					</FilterCategory>
				)}

				{/* Custom filters from CMS config */}
				{customFilterConfigs.map(cf => {
					const draftValues = draftCustomSelections[cf.slug] ?? []
					const summaryLabel = cf.type === 'select'
						? (draftValues.length > 0 ? cf.options.find(o => o.value === draftValues[0])?.label ?? 'Any' : 'Any')
						: (formatMultiLabel(draftValues, cf.options) ?? 'Any')

					return (
						<FilterCategory
							key={cf.slug}
							id={cf.slug}
							label={cf.name}
							valueSummary={summaryLabel}
							isExpanded={expanded.has(cf.slug)}
							onToggle={() => toggleCategory(cf.slug)}
						>
							{cf.type === 'select' ? (
								<SingleSelectContent
									options={cf.options}
									selected={draftValues.length > 0 ? draftValues[0] : null}
									onChange={value => setDraftCustomSelections(prev => ({ ...prev, [cf.slug]: value ? [value] : [] }))}
									maxHeight={null}
									ariaLabel={cf.name}
								/>
							) : (
								<MultiSelectContent
									options={cf.options}
									selected={draftValues}
									onChange={values => setDraftCustomSelections(prev => ({ ...prev, [cf.slug]: values }))}
									maxHeight={null}
									ariaLabel={cf.name}
								/>
							)}
						</FilterCategory>
					)
				})}
			</div>
		</Modal>
	)
}

/* ---------- Helpers ---------- */

function formatMultiLabel (
	selected: string[],
	options: { value: string; label: string }[]
): string | undefined {
	if (selected.length === 0) return undefined
	const first = options.find(o => o.value === selected[0])?.label ?? selected[0]
	return selected.length > 1 ? `${first} +${selected.length - 1}` : first
}

function formatShortDate (iso: string): string | undefined {
	if (!iso) return undefined
	const d = new Date(iso + 'T00:00:00')
	if (isNaN(d.getTime())) return iso
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
